import moment from 'moment-timezone';
import React from 'react';
import fromPairs from 'lodash/fromPairs';

import store from '../store';

import {
  userTypes,
  SCHOOL_USER,
  CANDIDATE,
  ADMINISTRATOR
} from '../constants/user';
import maritalStatuses from '../constants/marital-statuses';
import applicationStatuses from '../constants/application-statuses';
import applicationSubstatuses from '../constants/application-substatuses';
import momentDateFormats from '../constants/moment-date-formats';
import vacancyStatuses from '../constants/vacancy-statuses';

export function checkMaritalStatusIncludesPartner(maritalStatus) {
  const availableMaritalStatuses = [
    maritalStatuses.marriagePending,
    maritalStatuses.married,
    maritalStatuses.other,
    maritalStatuses.singleWithPartner
  ];

  if (maritalStatus === null || maritalStatus === undefined) {
    return false;
  }

  return availableMaritalStatuses.includes(parseInt(maritalStatus, 10));
}

export function getFullUserDisplayName(user) {
  let userData = user;

  if (user.toJS) {
    userData = user.toJS();
  }

  const { lastName, firstName, middleName } = userData;

  return prepareFullUserDisplayName(lastName, firstName, middleName);
}

export function prepareFullUserDisplayName(lastName, firstName, middleName) {
  return `${lastName || ''}, ${firstName || ''} ${middleName || ''}`.trim();
}

export function getVacancyDisplayName(vacancy) {
  return vacancy.vacancySubjects
    .map(subject => {
      if (!subject.percentage || subject.percentage === 100) {
        return subject.subjectName;
      }

      return `${subject.subjectName} ${subject.percentage}%`;
    })
    .join(', ');
}

export function parseHttpStatusText(statusText) {
  const matchResult = statusText.match(/^(\d*) (.*)$/);

  if (matchResult === null) {
    return { subcode: '', text: statusText };
  }

  return {
    // eslint-disable-next-line
    subcode: parseInt(matchResult[1]),
    text: matchResult[2]
  };
}

export function getRedirectAfterSignInPath(userType) {
  switch (userType) {
    case userTypes[SCHOOL_USER]:
      return '/schools/dashboard';
    case userTypes[CANDIDATE]:
      return '/candidate/my-applications';
    case userTypes[ADMINISTRATOR]:
      return '/admin';
    default:
      return '/public';
  }
}

export function getYearPickerOptions(startYear, endYear) {
  let options = [];

  for (let i = endYear; i >= startYear; i--) {
    options.push(i);
  }

  return options;
}

export function getMonthPickerOptions() {
  return fromPairs(moment.months().map((value, index) => [index + 1, value]));
}

export function getFileIconClass(fileName) {
  const extension = (fileName || '').split('.').pop();

  switch (extension && extension.toLowerCase()) {
    case 'pdf': {
      return 'i-apli-pdf';
    }
    case 'xsl':
    case 'xslx': {
      return 'i-apli-xlsx';
    }
    case 'docx':
    case 'doc': {
      return 'i-apli-doc';
    }
    case 'jpg': {
      return 'i-apli-image';
    }
    case 'zip': {
      return 'i-apli-zip';
    }
    case 'ppt':
    case 'pptx': {
      //TODO: not correct usage, but same image
      return 'i-apli-copy';
    }
    default: {
      //TODO: there is no default image currently
      return 'fa fa-file-o';
    }
  }
}

export function getTimeZoneDisplayName(timezoneName, now) {
  const currentDate = now || moment().format();

  const formattedTimeZone = moment.tz(currentDate, timezoneName).format('Z z');

  const [offset, abbreviation] = formattedTimeZone.split(' ');

  return `${timezoneName} ${offset} ${/^\w+$/.test(abbreviation)
    ? abbreviation
    : ''}`;
}

export function getCurrentTimeZone() {
  const { authUser } = store.getState();
  const accountSettings = authUser.get('accountSettings');

  return accountSettings
    ? accountSettings.get('ianaTimeZone')
    : moment.tz.guess();
}

//this method just changes timezone, not datetime value
export function getMomentWithTimeZone(date, timezone) {
  if (!date) {
    throw Error('date argument is required');
  }

  const currentTimezone = timezone || getCurrentTimeZone();

  const dateMoment =
    date instanceof moment ? date.clone() : moment.parseZone(date).clone();

  const result = moment.tz(
    dateMoment.format(momentDateFormats.fullDateTimeFormat),
    momentDateFormats.fullDateTimeFormat,
    currentTimezone
  );

  return result;
}

export function getCurrentMomentInTimeZone(timezone) {
  const currentTimezone = timezone || getCurrentTimeZone();

  return moment().tz(currentTimezone);
}

export function getSiteUrl() {
  return window.location.protocol
    .concat('//')
    .concat(window.location.hostname)
    .concat(':')
    .concat(window.location.port);
}

export function preserveStringLineBreaks(str) {
  if (!str) {
    return str;
  }

  var lines = str.split(/\n/g);

  for (let index = 1; index < lines.length; index += 2) {
    lines.splice(index, 0, <br key={index} />);
  }

  return lines;
}

export function makeCancelablePromise(promise) {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      val => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val)),
      error => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error))
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    }
  };
}

export function textFieldOnBlurTrimWrapper(input, onBlurEvent) {
  var target = onBlurEvent.target;

  target.value = !target.value ? target.value : target.value.trim();

  return input.onBlur(onBlurEvent);
}

export function buildEmailParameterPlaceholder(parameter) {
  return `@(${parameter})`;
}

export function getApplicationCardStatusClassName(status, substatus) {
  const baseStatusClassName = 'min-section__card_bordered-application-';

  const statusClassNames = {
    [applicationStatuses.active]: 'active',
    [applicationStatuses.decisionMade]: 'decision-made',
    [applicationStatuses.incomplete]: 'incomplete',
    [applicationStatuses.withdrawn]: 'withdrawn'
  };

  const statusClassName = baseStatusClassName + statusClassNames[status];

  if (!substatus) {
    return statusClassName;
  }

  const substatusClassNames = {
    [applicationSubstatuses.offerMade]: 'offer-made',
    [applicationSubstatuses.hired]: 'hired',
    [applicationSubstatuses.notAFit]: 'not-a-fit',
    [applicationSubstatuses.offerDeclined]: 'offer-declined',
    [applicationSubstatuses.possibleMatch]: 'possible-match',
    [applicationSubstatuses.shortlisted]: 'shortlisted'
  };

  const substatusClassName =
    baseStatusClassName + substatusClassNames[substatus];

  return `${statusClassName} ${substatusClassName}`;
}

export function getVacancyCardStatusClassName(status) {
  switch (status) {
    case vacancyStatuses.archived: {
      return 'min-section__card_bordered-archived';
    }
    case vacancyStatuses.closed: {
      return 'min-section__card_bordered-closed';
    }
    case vacancyStatuses.draft: {
      return 'min-section__card_bordered-draft';
    }
    case vacancyStatuses.onHold: {
      return 'min-section__card_bordered-on-hold';
    }
    case vacancyStatuses.posted: {
      return 'min-section__card_bordered-posted';
    }
    default: {
      return '';
    }
  }
}

export function getListSettingsFromQueryParams(queryParams) {
  const listSettings = { ...queryParams };

  delete listSettings.pageNumber;

  return listSettings;
}

export function generateFormId() {
  return generateHtmlSafeRandomId() + '_' + generateHtmlSafeRandomId();
}

export function generateHtmlSafeRandomId() {
  return Math.random()
    .toString()
    .replace(/^0[.,]+/, '');
}



// WEBPACK FOOTER //
// ./src/tools/index.js